import { flip, offset, shift } from '@floating-ui/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { ElementFormatType, NodeKey } from 'lexical';
import { $getNodeByKey, $isElementNode, FORMAT_ELEMENT_COMMAND } from 'lexical';
import { TextCursorInput, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNodeAnchoredFloating } from '../../hooks/useNodeAnchoredFloating';
import { ALIGNMENTS } from '../../ui/alignments';
import { EditorPortal } from '../../ui/EditorPortal';
import { panelClasses, toolbarButtonClasses } from '../../ui/styles';
import { ToolbarButton, ToolbarSeparator } from '../../ui/ToolbarButton';
import { AltTextForm } from './AltTextForm';
import { useMaxImageWidth, useUpdateImage } from './useUpdateImage';

const SIZE_PRESETS = [25, 50, 75, 100] as const;
/** Images sit inline, so justify means nothing for them. */
const IMAGE_ALIGNMENTS = ALIGNMENTS.filter(({ value }) => value !== 'justify');

/** Preset widths are rounded px, so allow a little slack when showing which one is active. */
const isWidth = (width: number | undefined, target: number) => width !== undefined && Math.abs(width - target) <= 2;

type ImageToolbarProps = { nodeKey: NodeKey; altText: string; width?: number };

/** Froala-style image popup above a selected image: preset widths, block alignment, alt text and remove. */
export const ImageToolbar = ({ nodeKey, altText, width }: ImageToolbarProps) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const updateImage = useUpdateImage(nodeKey);
  const maxWidth = useMaxImageWidth();
  const [editingAlt, setEditingAlt] = useState(false);
  const [alignment, setAlignment] = useState<ElementFormatType>('');

  const { refs, floatingStyles } = useNodeAnchoredFloating(nodeKey, {
    placement: 'top',
    middleware: [offset(10), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  // Alignment lives on the block around the image, so follow it as the document changes
  useEffect(() => {
    const read = () =>
      editor.getEditorState().read(() => {
        const block = $getNodeByKey(nodeKey)?.getTopLevelElement();
        setAlignment($isElementNode(block) ? block.getFormatType() : '');
      });
    read();
    return editor.registerUpdateListener(read);
  }, [editor, nodeKey]);

  return (
    <EditorPortal
      ref={refs.setFloating}
      style={floatingStyles}
      role="toolbar"
      aria-label={t('editor.image.options')}
      className={`${panelClasses} flex items-center gap-0.5 p-1`}
      // The alt-text input needs focus; everything else must keep the selection
      keepSelection={!editingAlt}
    >
      {editingAlt ? (
        <AltTextForm
          initial={altText}
          onCancel={() => setEditingAlt(false)}
          onApply={(value) => {
            updateImage((node) => node.setAltText(value));
            setEditingAlt(false);
          }}
        />
      ) : (
        <>
          {SIZE_PRESETS.map((percent) => {
            const target = Math.round((maxWidth() * percent) / 100);
            const label = t('editor.image.size-percent', { percent });
            return (
              <button
                key={percent}
                type="button"
                title={label}
                aria-label={label}
                aria-pressed={isWidth(width, target)}
                className={`${toolbarButtonClasses(isWidth(width, target), false)} text-pbk-12 min-w-9 font-medium`}
                onClick={() => updateImage((node) => node.setWidth(target))}
              >
                {percent}%
              </button>
            );
          })}
          <button
            type="button"
            title={t('editor.image.size-original')}
            aria-pressed={width === undefined}
            className={`${toolbarButtonClasses(width === undefined, false)} text-pbk-12 font-medium`}
            onClick={() => updateImage((node) => node.setWidth(undefined))}
          >
            {t('editor.image.size-original')}
          </button>

          <ToolbarSeparator />

          {IMAGE_ALIGNMENTS.map(({ value, icon, labelKey }) => (
            <ToolbarButton
              key={value}
              icon={icon}
              label={t(labelKey)}
              active={alignment === value || (value === 'left' && alignment === '')}
              onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)}
            />
          ))}

          <ToolbarSeparator />

          <ToolbarButton icon={TextCursorInput} label={t('editor.image.alt-text')} active={!!altText} onClick={() => setEditingAlt(true)} />
          <ToolbarButton icon={Trash2} label={t('editor.image.remove')} onClick={() => updateImage((node) => node.remove())} />
        </>
      )}
    </EditorPortal>
  );
};
